import { useState } from 'react';
import { BellRing, CalendarRange, MapPin, Megaphone, Pencil, Plus, TriangleAlert, Users, X } from 'lucide-react';
import {
  getApiError,
  useCancelEventMutation,
  useListEventsQuery,
  useListGroupsQuery,
  usePublishEventMutation,
  type EventStatus,
  type SchoolEvent,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Field, Input, Select } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { PageLayout } from '@/components/ui/PageLayout';
import { Spinner } from '@/components/ui/Spinner';
import { EVENT_CATEGORY_LABELS, formatEventWhen, relativeDay } from '@/lib/datetime';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { EventDialog, emptyEventDraft, toEventDraft, type EventDraft } from './EventDialog';
import { ResponsesDialog } from './ResponsesDialog';
import { RemindersDialog } from './RemindersDialog';

/*
  The school calendar.

  ── Why this is a list and not a month grid ────────────────────────────────

  A month grid is for a diary you scan; this is a worklist. The questions are
  "what is still a draft", "who has not replied to the sports day" and "is next
  week's meeting announced yet" — all of which are answered by a chronological
  list with state on each row, and none of which a 7×5 grid of truncated titles
  answers at all.

  ── Why drafts sit in the same list ────────────────────────────────────────

  Separating them into a tab means the thing you forgot to publish is on the
  screen you are not looking at.
*/
export function EventsPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');

  const [status, setStatus] = useState<EventStatus | ''>('');
  const [draft, setDraft] = useState<EventDraft | null>(null);
  const [responsesFor, setResponsesFor] = useState<string | null>(null);
  const [remindersFor, setRemindersFor] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<SchoolEvent | null>(null);
  const [banner, setBanner] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const { data: groups = [] } = useListGroupsQuery(institutionId, { skip: !institutionId });
  const { data: events = [], isLoading, isError, error } = useListEventsQuery(
    { institutionId, status: status || undefined },
    { skip: !institutionId },
  );

  const [publishEvent, { isLoading: publishing }] = usePublishEventMutation();

  const handlePublish = async (event: SchoolEvent) => {
    try {
      await publishEvent({ institutionId, eventId: event._id }).unwrap();
      setBanner({ tone: 'success', text: `"${event.title}" is published — parents can see it now.` });
    } catch (err) {
      setBanner({ tone: 'danger', text: getApiError(err) });
    }
  };

  const drafts = events.filter((e) => e.status === 'draft').length;

  return (
    <PageLayout
      title="Events"
      subtitle="The school calendar parents see"
      breadcrumbs={[{ label: 'Communication' }, { label: 'Events' }]}
      contained={false}
      actions={
        <Button icon={<Plus size={13} />} onClick={() => setDraft(emptyEventDraft())}>
          New event
        </Button>
      }
      toolbar={
        <>
          <Select value={status} onChange={(e) => setStatus(e.target.value as EventStatus | '')} className="w-auto">
            <option value="">All events</option>
            <option value="draft">Drafts</option>
            <option value="published">Published</option>
            <option value="cancelled">Cancelled</option>
          </Select>
          {drafts > 0 && status !== 'draft' && (
            <button
              type="button"
              onClick={() => setStatus('draft')}
              className={cn(styles.markWarning, 'normal-case tracking-normal text-[11px]')}
            >
              {drafts} unpublished draft{drafts === 1 ? '' : 's'}
            </button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {banner && (
          <div
            className={cn(
              banner.tone === 'success' ? styles.markSuccess : styles.markDanger,
              'w-full justify-between normal-case tracking-normal text-[11px]',
            )}
          >
            <span className="flex items-center gap-1.5">
              {banner.tone === 'danger' && <TriangleAlert size={12} className="flex-shrink-0" />}
              {banner.text}
            </span>
            <button type="button" onClick={() => setBanner(null)} className="font-bold opacity-60 hover:opacity-100">
              Dismiss
            </button>
          </div>
        )}

        {isLoading ? (
          <div className={cn(styles.panel, 'flex justify-center py-12')}>
            <Spinner />
          </div>
        ) : isError ? (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <EmptyState icon={<TriangleAlert size={17} />} title="Could not load events" description={getApiError(error)} />
          </div>
        ) : events.length === 0 ? (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <EmptyState
              icon={<CalendarRange size={17} />}
              title={status ? 'Nothing here' : 'No events yet'}
              description={
                status
                  ? 'Try a different status.'
                  : 'Create a sports day, a parents meeting or a holiday. It is saved as a draft until you publish it.'
              }
              action={
                !status ? (
                  <Button size="sm" variant="secondary" icon={<Plus size={12} />} onClick={() => setDraft(emptyEventDraft())}>
                    New event
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          events.map((event) => (
            <EventCard
              key={event._id}
              event={event}
              publishing={publishing}
              onEdit={() => setDraft(toEventDraft(event))}
              onPublish={() => void handlePublish(event)}
              onCancel={() => setCancelling(event)}
              onResponses={() => setResponsesFor(event._id)}
              onReminders={() => setRemindersFor(event._id)}
            />
          ))
        )}
      </div>

      <EventDialog draft={draft} groups={groups} institutionId={institutionId} onClose={() => setDraft(null)} />
      <ResponsesDialog eventId={responsesFor} institutionId={institutionId} onClose={() => setResponsesFor(null)} />
      <RemindersDialog eventId={remindersFor} institutionId={institutionId} onClose={() => setRemindersFor(null)} />
      <CancelDialog
        event={cancelling}
        institutionId={institutionId}
        onClose={() => setCancelling(null)}
        onDone={(title) => setBanner({ tone: 'success', text: `"${title}" is cancelled. Parents can see the reason.` })}
      />
    </PageLayout>
  );
}

function EventCard({
  event,
  publishing,
  onEdit,
  onPublish,
  onCancel,
  onResponses,
  onReminders,
}: {
  event: SchoolEvent;
  publishing: boolean;
  onEdit: () => void;
  onPublish: () => void;
  onCancel: () => void;
  onResponses: () => void;
  onReminders: () => void;
}) {
  const cancelled = event.status === 'cancelled';
  const replied = event.counts.going + event.counts.notGoing;

  return (
    <div className={cn(styles.panel, cancelled && 'opacity-70')}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={cn('text-[13px] font-bold text-foreground', cancelled && 'line-through')}>
              {event.title}
            </h2>
            {event.status === 'draft' && <Mark tone="warning">Draft</Mark>}
            {event.status === 'published' && <Mark tone="success">Published</Mark>}
            {cancelled && <Mark tone="danger">Cancelled</Mark>}
            <Mark tone="neutral">{EVENT_CATEGORY_LABELS[event.category] ?? event.category}</Mark>
          </div>

          <p className={cn(styles.meta, 'mt-1')}>
            {formatEventWhen(event.startAt, event.endAt, event.allDay)}
            <span className="opacity-70"> · {relativeDay(event.startAt)}</span>
          </p>

          <div className={cn(styles.meta, 'mt-1 flex flex-wrap items-center gap-x-3 gap-y-1')}>
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin size={11} /> {event.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users size={11} />
              {event.groupNames.length === 0 ? 'Whole school' : event.groupNames.join(', ')}
              {` · ${event.counts.audience} child${event.counts.audience === 1 ? '' : 'ren'}`}
            </span>
          </div>

          {event.description && (
            <p className="mt-1.5 max-w-2xl text-[11.5px] leading-relaxed text-muted-foreground">
              {event.description}
            </p>
          )}

          {cancelled && event.cancellationReason && (
            <p className={cn(styles.markDanger, 'mt-2 inline-flex normal-case tracking-normal text-[11px]')}>
              {event.cancellationReason}
            </p>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {event.rsvpRequired && event.status === 'published' && (
            <button
              type="button"
              onClick={onResponses}
              className={cn(styles.panel, 'px-2.5 py-1 text-left transition-colors hover:bg-muted')}
            >
              <p className={styles.label}>Replies</p>
              <p className={cn(styles.figure, 'text-[12.5px]')}>
                {replied}
                <span className="font-normal text-muted-foreground">/{event.counts.audience}</span>
                {event.counts.going > 0 && (
                  <span className="ml-1.5 text-[11px] font-semibold text-[hsl(var(--record-ink-success))]">
                    {event.counts.going} going
                  </span>
                )}
              </p>
            </button>
          )}

          {event.reminderOffsetsHours.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              icon={<BellRing size={12} />}
              onClick={onReminders}
              aria-label={`Reminders for ${event.title}`}
            >
              {event.reminderOffsetsHours.length}
            </Button>
          )}

          {event.status === 'draft' && (
            <Button size="sm" icon={<Megaphone size={12} />} loading={publishing} onClick={onPublish}>
              Publish
            </Button>
          )}
          {!cancelled && (
            <>
              <Button size="sm" variant="ghost" iconOnly aria-label={`Edit ${event.title}`} onClick={onEdit}>
                <Pencil size={13} />
              </Button>
              <Button size="sm" variant="ghost" iconOnly aria-label={`Cancel ${event.title}`} onClick={onCancel}>
                <X size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/*
  Cancelling asks for a reason because the API requires one — and the API
  requires one because "Sports day cancelled" with no explanation generates
  exactly the phone calls the announcement existed to prevent.
*/
function CancelDialog({
  event,
  institutionId,
  onClose,
  onDone,
}: {
  event: SchoolEvent | null;
  institutionId: string;
  onClose: () => void;
  onDone: (title: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [cancelEvent, { isLoading }] = useCancelEventMutation();

  const [forEvent, setForEvent] = useState<string | null>(null);
  if (event && forEvent !== event._id) {
    setForEvent(event._id);
    setReason('');
    setError('');
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;
    if (!reason.trim()) return setError('Parents see this — say why.');
    try {
      await cancelEvent({ institutionId, eventId: event._id, reason: reason.trim() }).unwrap();
      onDone(event.title);
      onClose();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <Modal
      open={event !== null}
      onClose={onClose}
      title="Cancel event"
      description={
        event?.status === 'published'
          ? 'Parents keep seeing it, struck through, with your reason.'
          : 'This draft will be marked cancelled rather than deleted.'
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Keep it
          </Button>
          <Button form="cancel-event-form" type="submit" variant="danger" loading={isLoading}>
            Cancel event
          </Button>
        </div>
      }
    >
      <form id="cancel-event-form" onSubmit={submit} className="flex flex-col gap-2.5">
        {error && (
          <div className={cn(styles.markDanger, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
            <TriangleAlert size={12} className="flex-shrink-0" />
            {error}
          </div>
        )}
        <Field label="Reason" required hint="Shown to every parent">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Hall double-booked — new date to follow."
            autoFocus
          />
        </Field>
      </form>
    </Modal>
  );
}
